import ClassNames from "classnames";
import { FunctionComponent } from "react";
import { getActiveSite } from "../selectors/sites.js";
import { useAppSelector } from "../store/hooks.js";
import buttonStyles from "../stylesheets/components/button.module.css";
import styles from "../stylesheets/components/module-consent-box.module.css";
import { Language, UsercentricsServiceName } from "../types/index.js";
import { css } from "../utils/emotion.js";
import { getUsercentricsServiceConsents } from "../utils/usercentrics.js";
import { getActiveColorScheme, getTranslations } from "../utils/utils.js";

interface Props {
  languageId: Language;
  serviceName: UsercentricsServiceName;
  onConsentGiven: () => void;
}

const translations = {
  de: {
    title: "Wir brauchen Ihr Einverständnis!",
    text: (
      serviceName: string,
    ) => `Wir benutzen Drittanbieter (${serviceName}), um Inhalte einzubinden.
      Diese können persönliche Daten über Ihre Aktivitäten sammeln.
      Bitte beachten Sie die Details und geben Sie Ihre Einwilligung.`,
    moreInfo: "Mehr Informationen",
    accept: "Akzeptieren",
  },
  en: {
    title: "We need your consent!",
    text: (
      serviceName: string,
    ) => `We rely on third party providers (${serviceName}) to integrate the content.
      They may collect personal information about your activities.
      Please note the details and give your consent.`,
    moreInfo: "More information",
    accept: "Accept",
  },
  it: {
    title: "Abbiamo bisogno del Suo consenso!",
    text: (
      serviceName: string,
    ) => `Per l’integrazione dei contenuti ci avvaliamo di fornitori terzi (${serviceName})
      che possono raccogliere dati personali sulle Sue attività.
      La preghiamo di prendere nota dei dettagli e di dare il Suo consenso.`,
    moreInfo: "Ulteriori informazioni",
    accept: "Accettare/acconsentire",
  },
};

const ModuleConsentBox: FunctionComponent<Props> = ({
  languageId,
  serviceName,
  onConsentGiven,
}) => {
  const i18n = getTranslations(languageId, translations);
  const scheme = useAppSelector((state) =>
    getActiveColorScheme(state.colorSchemes, getActiveSite(state.sites)),
  );

  return (
    <div className={styles.ModuleConsentBox}>
      <div
        className={ClassNames(
          styles.ModuleConsentBox__Modal,
          "TextAlign--center",
          css({
            backgroundColor: scheme.main.background,
            color: scheme.main.text,
          }),
        )}
      >
        <h2 className={styles.ModuleConsentBox__Title}>{i18n.title}</h2>
        <p>{serviceName && i18n.text(serviceName)}</p>

        <div
          className={ClassNames(
            buttonStyles.ButtonGroup,
            buttonStyles["ButtonGroup--align-center"],
          )}
        >
          <button
            className={ClassNames(
              styles["ModuleConsentBox__Button--more"],
              buttonStyles.Button,
              "Btn--bare",
              css({
                backgroundColor: scheme.secondary.background,
                borderColor: scheme.secondary.background,
                color: scheme.secondary.text,
              }),
            )}
            onClick={() => {
              window.__ucCmp?.showSecondLayer();
            }}
          >
            {i18n.moreInfo}
          </button>
          <button
            type="button"
            className={ClassNames(
              styles["ModuleConsentBox__Button--accept"],
              buttonStyles.Button,
              "Btn--bare",
              css({
                backgroundColor: scheme.primary.background,
                borderColor: scheme.primary.background,
                color: scheme.primary.text,
              }),
            )}
            onClick={async () => {
              const id = await getUsercentricsServiceConsents(serviceName);
              if (!id) return;
              window.__ucCmp.updateServicesConsents(id);

              onConsentGiven();
            }}
          >
            {i18n.accept}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ModuleConsentBox;
